import React from 'react'
import { useHistory } from 'react-router-dom'
import { userRoles } from 'config/constants'
import { DesactivationForm, DerivarForm } from 'components'
import Text from 'components/common/Text'
import Request from 'api'
import useMergedState from './useMergedState'

const confirmationModalProps = {
  title: `¿Estás seguro que deseas cancelar la creación de esta Experiencia?`,
  titleType: `h2`,
  maxWidth: `900px`,
  maxHeight: `auto`,
  withClose: false,
  titleClass: `desactivationTitle`,
  withButtons: false,
}

/**
 * Essencial hook to handle Actions from experience
 *
 * @export
 * @param {*} [initialState={}]
 * @returns [State, Dispatcher]
 */
export default function useActionHandlers({ role, refreshPage }) {
  const { push } = useHistory()
  const [{ modalProps, message }, setState] = useMergedState({
    modalProps: null,
    message: null,
  })

  function closeModal() {
    setState({
      modalProps: null,
    })
  }

  function setMessage(message, type = `error`) {
    setState({
      message: {
        type,
        message,
        setError: () => {
          setState({ message: null })
        },
      },
    })
  }

  function removeFromLine(experienceId) {
    const index = window.experiencesToActivate.indexOf(experienceId)
    delete window.experiencesToActivate[index]
    window.experiencesToActivate = window.experiencesToActivate.filter(id => id)
  }

  function canActivateExp(experienceId) {
    if (!window.experiencesToActivate) {
      window.experiencesToActivate = [experienceId]
      return true
    }
    if (!window.experiencesToActivate.includes(experienceId)) {
      window.experiencesToActivate = [...window.experiencesToActivate, experienceId]
      return true
    }
    return false
  }

  function activateExperience(experienceId, role) {
    if (canActivateExp(experienceId)) {
      const requestPrefix = role.toLowerCase()
      const activation = new Request({
        endpoint: `${requestPrefix}Desactivate`,
        params: {
          experienceId,
          activate: true,
        },
      })
      activation
        .send()
        .then(({ message }) => {
          setMessage(message, `success`)
        })
        .catch(({ message }) => {
          setMessage(message)
        })
        .finally(() => {
          removeFromLine(experienceId)
          refreshPage()
        })
    }
  }

  function deleteExperience(experienceId) {
    new Request({
      endpoint: `providerDelete`,
    })
      .send(experienceId)
      .then(({ message }) => {
        setMessage(message, `success`)
      })
      .catch(({ message }) => {
        setMessage(message)
      })
      .finally(() => {
        refreshPage()
      })
  }

  function deleteExpUpgrade(experienceId) {
    new Request({
      endpoint: `providerDeleteUpgrade`,
    })
      .send(experienceId)
      .then(({ message }) => {
        setMessage(message, `success`)
      })
      .catch(({ message }) => {
        setMessage(message)
      })
      .finally(() => {
        refreshPage()
      })
  }

  function pauseExperience(experienceId, pause) {
    if (canActivateExp(experienceId)) {
      const pauseRequest = new Request({
        endpoint: `providerPause`,
        params: {
          experienceId,
          pause,
        },
      })
      pauseRequest
        .send()
        .then(({ message }) => {
          setMessage(message, `success`)
        })
        .catch(({ message }) => {
          setMessage(message)
        })
        .finally(() => {
          removeFromLine(experienceId)
          refreshPage()
        })
    }
  }

  function callActionHandler({ triggeredAction: action, id: experienceId }) {
    const actionsByRole = {
      [userRoles.Admin]: {
        desactivate: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Desactivación de experiencia`,
              children: (
                <DesactivationForm
                  onCloseModal={closeModal}
                  role={userRoles.Admin}
                  experienceId={experienceId}
                  setMessage={setMessage}
                  refreshPage={refreshPage}
                />
              ),
            },
          })
        },
        play: () => {
          pauseExperience(experienceId, false)
        },
        delete: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Eliminar una experiencia`,
              maxWidth: `600px`,
              onClose: closeModal,
              onAccept: () => deleteExperience(experienceId, userRoles.Validator),
              withButtons: true,
              children: <Text titleText={`¿Esta seguro de eliminar esta Experiencia?`} />,
            },
          })
        },
        derivar: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Derivar una experiencia`,
              children: (
                <DerivarForm
                  onCloseModal={closeModal}
                  role={userRoles.Validator}
                  experienceId={experienceId}
                  setMessage={setMessage}
                  refreshPage={refreshPage}
                />
              ),
            },
          })
        },
        edit: () => {
          push(`/admin/edit-experience/${experienceId}`)
        },
        activate: () => {
          activateExperience(experienceId, userRoles.Admin)
        },
      },
      [userRoles.Validator]: {
        validate: () => {
          push(`/usuario/validate-experience/${experienceId}`)
        },
        desactivate: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Desactivación de experiencia`,
              children: (
                <DesactivationForm
                  onCloseModal={closeModal}
                  role={userRoles.Validator}
                  experienceId={experienceId}
                  setMessage={setMessage}
                  refreshPage={refreshPage}
                />
              ),
            },
          })
        },
        activate: () => {
          activateExperience(experienceId, userRoles.Validator)
        },
        derivar: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Derivar una experiencia`,
              children: (
                <DerivarForm
                  onCloseModal={closeModal}
                  role={userRoles.Validator}
                  experienceId={experienceId}
                  setMessage={setMessage}
                  refreshPage={refreshPage}
                />
              ),
            },
          })
        },
        edit: () => {
          push(`/proveedor/edit-experience/${experienceId}`)
        },
      },
      [userRoles.ValidatorNacional]: {
        validate: () => {
          push(`/validadornacional/validate-experience/${experienceId}`)
        },
        edit: () => {
          push(`/validadornacional/edit-experience/${experienceId}`)
        },
      },
      [userRoles.Provider]: {
        desactivate: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Desactivación de experiencia`,
              children: (
                <DesactivationForm
                  onCloseModal={closeModal}
                  role={userRoles.Admin}
                  experienceId={experienceId}
                  setMessage={setMessage}
                  refreshPage={refreshPage}
                />
              ),
            },
          })
        },
        edit: () => {
          push(`/proveedor/edit-experience/${experienceId}`)
        },
        delete: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Eliminar una experiencia`,
              maxWidth: `600px`,
              onClose: closeModal,
              onAccept: () => deleteExperience(experienceId, userRoles.Validator),
              withButtons: true,
              children: <Text titleText={`¿Esta seguro de eliminar esta Experiencia?`} />,
            },
          })
        },
        deleteupgrade: () => {
          setState({
            modalProps: {
              ...confirmationModalProps,
              title: `Eliminar Mejora de Experiencia`,
              maxWidth: `600px`,
              onClose: closeModal,
              onAccept: () => deleteExpUpgrade(experienceId),
              withButtons: true,
              children: (
                <Text
                  titleText={`¿Esta seguro que desea eliminar la mejora de esta Experiencia?`}
                />
              ),
            },
          })
        },
        upgrade: () => {
          push(`/proveedor/upgrade-experience/${experienceId}`)
        },
        pause: () => {
          pauseExperience(experienceId, true)
        },
        play: () => {
          pauseExperience(experienceId, false)
        },
        watch: () => {
          push(`/proveedor/edit-experience/${experienceId}`)
        },
      },
    }
    actionsByRole[userRoles[role]][action.toLowerCase()]()
  }

  function actionListener({ action: triggeredAction, experienceId: id }) {
    callActionHandler({ triggeredAction, id })
  }

  return [actionListener, { modalProps, message }]
}
